import {
  type FreeScoutReference,
  type Me,
  PHONE_ROUTE_SENTENCE,
  SignInDenialReasonSchema,
} from "@hoffman-support/contracts";
import { useEffect, useState } from "react";
import { getMe, signOut } from "./api";
import { canUseFreeScout, freeScoutRoute } from "./freescout-api";
import { AdminHealth } from "./views/AdminHealth";
import { AdminSettings } from "./views/AdminSettings";
import { FreeScoutHandoff } from "./views/FreeScoutHandoff";
import { RequestDetail } from "./views/RequestDetail";
import { RequestForm } from "./views/RequestForm";
import { RequestList } from "./views/RequestList";
import { StaffQueue } from "./views/StaffQueue";
import { StaffRequestDetail } from "./views/StaffRequestDetail";
import { StaffTriageQueue } from "./views/StaffTriageQueue";
import { StaffTriageRun } from "./views/StaffTriageRun";

type Session = { state: "loading" } | { state: "anonymous" } | { state: "signed-in"; me: Me };

const DENIAL_TEXT: Record<string, string> = {
  tenant_not_allowed: "Your organisation is not enrolled in this portal.",
  no_role:
    "Your account has no portal role assigned. Ask your administrator to add you to the requester group.",
  privileged_role:
    "Administrative accounts cannot use the portal. Sign in with your standard user account.",
  privileged_upn:
    "Administrative accounts cannot use the portal. Sign in with your standard user account.",
  invalid_token: "The sign-in response could not be verified. Please try again.",
  invalid_state: "The sign-in attempt expired or was already used. Please start again.",
  exchange_failed: "Sign-in could not be completed. Please try again.",
};

function SignInDenied() {
  const reason = new URLSearchParams(window.location.search).get("reason") ?? "";
  const parsed = SignInDenialReasonSchema.safeParse(reason);
  const text = parsed.success ? DENIAL_TEXT[parsed.data] : "Sign-in was refused.";
  return (
    <section aria-labelledby="denied-heading">
      <h2 id="denied-heading">Sign-in refused</h2>
      <p>{text}</p>
      <p>{PHONE_ROUTE_SENTENCE}</p>
      <p>
        <a href="/auth/login">Try again</a>
      </p>
    </section>
  );
}

type View =
  | { view: "list" }
  | { view: "new" }
  | { view: "detail"; id: string }
  | { view: "denied" }
  | { view: "staff-queue" }
  | { view: "staff-new" }
  | { view: "staff-detail"; id: string }
  | { view: "freescout"; reference: FreeScoutReference | null }
  | { view: "triage-queue" }
  | { view: "triage-run"; runId: string }
  | { view: "admin-settings" }
  | { view: "admin-health" };

/** Deep links are served by the CloudFront function as index.html; the path decides the view. */
function route(path: string): View {
  if (path === "/sign-in-denied") return { view: "denied" };
  if (path === "/requests/new") return { view: "new" };
  if (path === "/staff" || path === "/staff/") return { view: "staff-queue" };
  if (path === "/staff/requests/new") return { view: "staff-new" };
  if (path.startsWith("/staff/freescout/"))
    return { view: "freescout", reference: freeScoutRoute(path) };
  if (path === "/staff/triage" || path === "/staff/triage/") return { view: "triage-queue" };
  if (path === "/admin/settings") return { view: "admin-settings" };
  if (path === "/admin/health") return { view: "admin-health" };
  const triageRun = path.match(/^\/staff\/triage\/([A-Za-z0-9-]+)$/);
  if (triageRun?.[1]) return { view: "triage-run", runId: triageRun[1] };
  const staffDetail = path.match(/^\/staff\/requests\/([A-Za-z0-9-]+)$/);
  if (staffDetail?.[1]) return { view: "staff-detail", id: staffDetail[1] };
  const detail = path.match(/^\/requests\/([A-Za-z0-9-]+)$/);
  if (detail?.[1]) return { view: "detail", id: detail[1] };
  return { view: "list" };
}

function isStaff(me: Me): boolean {
  return me.user.roles.some((r) => r === "staff" || r === "admin" || r === "leadership");
}

export function App() {
  const [session, setSession] = useState<Session>({ state: "loading" });
  const current = route(window.location.pathname);

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((me) => {
        if (!cancelled) setSession(me ? { state: "signed-in", me } : { state: "anonymous" });
      })
      .catch(() => {
        if (!cancelled) setSession({ state: "anonymous" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function body(): React.ReactNode {
    if (current.view === "denied") return <SignInDenied />;
    if (session.state === "loading") {
      return (
        <p role="status" aria-live="polite">
          Checking your session…
        </p>
      );
    }
    if (session.state === "anonymous") {
      return (
        <section aria-labelledby="signin-heading">
          <h2 id="signin-heading">Sign in</h2>
          <p>
            <a href={`/auth/login?returnTo=${encodeURIComponent(window.location.pathname)}`}>
              Sign in with your work account
            </a>
          </p>
          <p>{PHONE_ROUTE_SENTENCE}</p>
        </section>
      );
    }
    const me = session.me;
    const staffOnly = <p role="alert">Staff only.</p>;
    switch (current.view) {
      case "new":
        return <RequestForm me={me} mode="requester" />;
      case "detail":
        return <RequestDetail me={me} id={current.id} />;
      case "staff-queue":
        return isStaff(me) ? <StaffQueue /> : staffOnly;
      case "staff-new":
        return isStaff(me) ? <RequestForm me={me} mode="staff" /> : staffOnly;
      case "staff-detail":
        return isStaff(me) ? <StaffRequestDetail me={me} id={current.id} /> : staffOnly;
      case "freescout":
        return canUseFreeScout(me) ? (
          current.reference ? (
            <FreeScoutHandoff me={me} reference={current.reference} />
          ) : (
            <p role="alert">
              This FreeScout conversation reference is not valid. Open the handoff link from
              FreeScout again.
            </p>
          )
        ) : (
          staffOnly
        );
      case "triage-queue":
        return isStaff(me) ? <StaffTriageQueue /> : staffOnly;
      case "triage-run":
        return isStaff(me) ? <StaffTriageRun me={me} runId={current.runId} /> : staffOnly;
      case "admin-settings":
        return isStaff(me) ? <AdminSettings me={me} /> : staffOnly;
      case "admin-health":
        return isStaff(me) ? <AdminHealth me={me} /> : staffOnly;
      default:
        return <RequestList />;
    }
  }

  return (
    <main
      style={{
        fontFamily: "system-ui, sans-serif",
        maxWidth: 880,
        margin: "2rem auto",
        padding: "0 1rem",
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ margin: 0 }}>
          <a href="/" style={{ textDecoration: "none", color: "inherit" }}>
            OP Solutions Support
          </a>
        </h1>
        {session.state === "signed-in" ? (
          <nav
            aria-label="Account"
            style={{ display: "flex", gap: "0.75rem", alignItems: "baseline" }}
          >
            <a href="/">My requests</a>
            {isStaff(session.me) ? <a href="/staff">Queue</a> : null}
            {isStaff(session.me) ? <a href="/staff/triage">Triage</a> : null}
            {isStaff(session.me) ? <a href="/admin/settings">Settings</a> : null}
            {isStaff(session.me) ? <a href="/admin/health">Health</a> : null}
            <span>{session.me.user.displayName}</span>
            <button
              type="button"
              onClick={() => {
                void signOut(session.me).then(() => window.location.assign("/"));
              }}
            >
              Sign out
            </button>
          </nav>
        ) : null}
      </header>
      {body()}
    </main>
  );
}
